import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  addBlacklist as dbAddBlacklist,
  listBlacklist,
  removeBlacklist as dbRemoveBlacklist,
} from '@/db/blacklist';
import type { BlacklistRow } from '@/db/schema';
import type { ComicCard, ComicDetails, SourceId, Tag } from '@/sources/types';

/**
 * Normalize a tag input or Tag object into the canonical "[namespace:]name"
 * form used for matching. Lower-cased; whitespace trimmed.
 *
 * Examples:
 *   "Tag"                    -> "tag"
 *   " parody : Foo "         -> "parody:foo"
 *   { namespace: 'tag', name: 'Big breasts' } -> "tag:big breasts"
 *   { name: 'Yuri' }         -> "yuri"
 */
export function normalizeTag(t: string | Pick<Tag, 'namespace' | 'name'>): string {
  if (typeof t === 'string') {
    const trimmed = t.trim().toLowerCase();
    if (!trimmed.includes(':')) return trimmed;
    const [ns, ...rest] = trimmed.split(':');
    return `${ns!.trim()}:${rest.join(':').trim()}`;
  }
  const name = t.name.trim().toLowerCase();
  if (t.namespace) return `${t.namespace.trim().toLowerCase()}:${name}`;
  return name;
}

/**
 * Compose the set of canonicalized tag strings on a comic, used for the
 * blacklist check. Includes both `namespace:name` and the bare `name` form
 * so a global "yuri" entry matches a `tag:yuri` on the comic and vice versa.
 */
export function comicTagSet(comic: { tags?: Tag[] }): Set<string> {
  const out = new Set<string>();
  for (const tag of comic.tags ?? []) {
    out.add(normalizeTag(tag));
    if (tag.namespace) out.add(tag.name.trim().toLowerCase());
  }
  return out;
}

export interface UseBlacklist {
  /** All rows currently in the blacklist store. */
  rows: BlacklistRow[];
  /** True while loading from IndexedDB. */
  loading: boolean;
  /** Force a reload from IndexedDB. */
  reload: () => void;
  /** Add a tag; `source = null` (default) means "all sources". */
  add: (tag: string, source?: string | null) => Promise<void>;
  /** Remove a tag by its canonical key. */
  remove: (tag: string, source: string | null) => Promise<void>;
  /**
   * Returns true if the given comic has any tag matching the blacklist for
   * its source. Cheap; runs over pre-built sets.
   */
  isBlocked: (comic: ComicCard | ComicDetails) => boolean;
  /** Returns the list of canonical tag strings that matched, for UI labels. */
  matchedTags: (comic: ComicCard | ComicDetails) => string[];
  /**
   * Filter a list of cards; drops blocked entries. Convenience wrapper for
   * source-list rendering. To preserve blocked items as a placeholder, use
   * {@link useFilteredComics} instead.
   */
  filterComics: <T extends ComicCard>(cards: T[]) => T[];
}

/**
 * Subscribe to the blacklist store. Exposes the raw rows for the settings
 * UI and pre-computes per-source tag sets so `isBlocked` is O(tags-on-comic).
 *
 * Note: this is _not_ a global cache. Multiple consumers each load their own
 * copy; the store is small (typically <50 entries) so the duplication is
 * negligible. If list pages start re-rendering noticeably we can lift this
 * into a context.
 */
export function useBlacklist(): UseBlacklist {
  const [rows, setRows] = useState<BlacklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listBlacklist()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const perSourceSets = useMemo(() => {
    /** Cached `Set<canonicalTag>` keyed by source id; the `*` slot is the
     * all-sources entries. Consumers ask for `union(global, perSource)`. */
    const global = new Set<string>();
    const bySource = new Map<SourceId, Set<string>>();
    for (const row of rows) {
      const tag = row.tag.toLowerCase();
      if (row.source === null) {
        global.add(tag);
      } else {
        const s = (bySource.get(row.source as SourceId) ?? new Set<string>());
        s.add(tag);
        bySource.set(row.source as SourceId, s);
      }
    }
    return { global, bySource };
  }, [rows]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  const add = useCallback(
    async (tag: string, source: string | null = null) => {
      const canonical = normalizeTag(tag);
      if (!canonical) return;
      await dbAddBlacklist(canonical, source);
      reload();
    },
    [reload],
  );

  const remove = useCallback(
    async (tag: string, source: string | null) => {
      await dbRemoveBlacklist(tag, source);
      reload();
    },
    [reload],
  );

  const matchedTags = useCallback(
    (comic: ComicCard | ComicDetails): string[] => {
      const onComic = comicTagSet(comic);
      const block = new Set<string>();
      for (const t of perSourceSets.global) {
        if (onComic.has(t)) block.add(t);
      }
      const perSource = perSourceSets.bySource.get(comic.source as SourceId);
      if (perSource) {
        for (const t of perSource) {
          if (onComic.has(t)) block.add(t);
        }
      }
      return [...block];
    },
    [perSourceSets],
  );

  const isBlocked = useCallback(
    (comic: ComicCard | ComicDetails): boolean => matchedTags(comic).length > 0,
    [matchedTags],
  );

  const filterComics = useCallback(
    <T extends ComicCard>(cards: T[]): T[] => cards.filter((c) => !isBlocked(c)),
    [isBlocked],
  );

  return { rows, loading, reload, add, remove, isBlocked, matchedTags, filterComics };
}

export interface FilteredComicsResult<T extends ComicCard> {
  /** Visible cards after filtering. */
  visible: T[];
  /** Cards that matched the blacklist; kept around so a "show anyway" UI can
   * surface a count without re-fetching. */
  blocked: T[];
  loading: boolean;
}

/**
 * Convenience hook for list pages: takes a raw `cards` list and a source id,
 * returns `{ visible, blocked, loading }`. `sourceId` is required because the
 * per-source blacklist entries depend on it.
 *
 * Phase 3 integration: source list pages (`SourcePage`, `FavoritesPage`,
 * etc.) should pass the raw card list through this hook before rendering.
 */
export function useFilteredComics<T extends ComicCard>(
  cards: T[],
  _sourceId: string,
): FilteredComicsResult<T> {
  const { isBlocked, loading } = useBlacklist();
  return useMemo(() => {
    const visible: T[] = [];
    const blocked: T[] = [];
    for (const c of cards) {
      if (isBlocked(c)) blocked.push(c);
      else visible.push(c);
    }
    return { visible, blocked, loading };
  }, [cards, isBlocked, loading]);
}
